import { useState, useEffect } from 'react'
import { ArrowLeft, ArrowRight, User, Loader2 } from 'lucide-react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import { handleValidationErrors } from '@/shared/utils'
import { validateStep3 } from '../gaming-addiction/validation/injuredPartyValidation'
import PersonalInfo from './PersonalInfo'
import ContactInfo from './ContactInfo'
import IdentityInfo from './IdentityInfo'
import LegalInfo from './LegalInfo'
import { EnrollmentApi, type Step3InjuredPartyProps, type ValidationErrors } from '@/features/enrollment'

// PFAS-specific validation (simpler than gaming addiction)
const validatePFASStep = (data: any) => {
  const errors: { [key: string]: string } = {}
  
  if (!data.injuredPartyFirstName) errors.injuredPartyFirstName = 'First Name is required'
  if (!data.injuredPartyLastName) errors.injuredPartyLastName = 'Last Name is required'
  if (!data.injuredPartyEmail) errors.injuredPartyEmail = 'Email is required'
  if (!data.gender) errors.gender = 'Gender is required'
  if (!data.injuredPartyAddressLine1) errors.injuredPartyAddressLine1 = 'Address Line 1 is required'
  if (!data.injuredPartyCity) errors.injuredPartyCity = 'City is required'
  if (!data.injuredPartyState) errors.injuredPartyState = 'State is required'
  if (!data.injuredPartyZipCode) errors.injuredPartyZipCode = 'ZIP Code is required'
  if (!data.phoneNumber) errors.phoneNumber = 'Phone Number is required'
  if (!data.injuredPartyDOB) errors.injuredPartyDOB = 'Date of Birth is required'
  
  return errors
}

export default function Step3InjuredParty({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  handleSimpleNavigate,
  handleNavigateWithCompletion,
  currentStepIndex,
  totalSteps = 10,
  useStageWrapper,
  isNavigating,
  isCompleting
}: Step3InjuredPartyProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false)

  const isRobloxCampaign =
    enrollmentData.campaignId === 'roblox-exploitation' ||
    enrollmentData.selectedCampaign === 'robloxExploitation' ||
    enrollmentData.campaign?.name?.toLowerCase().includes('roblox')

  const computedCurrentStepIndex = currentStepIndex ?? (isRobloxCampaign ? 1 : 2)
  const computedTotalSteps = totalSteps
  const computedNextStepIndex = Math.min(computedTotalSteps - 1, computedCurrentStepIndex + 1)
  const computedPreviousStepIndex = Math.max(0, computedCurrentStepIndex - 1)
  const shouldUseStageWrapper = useStageWrapper ?? isRobloxCampaign
  const handleStageNavigate = async (targetIndex: number) => {
    if (targetIndex < computedCurrentStepIndex && handleSimpleNavigate) {
      handleSimpleNavigate(targetIndex)
      return
    }

    await handleNavigate(targetIndex)
  }

  // Auto-populate fields from Step2 data (only once on mount)
  useEffect(() => {
    if (!hasAutoPopulated && enrollmentData.intakeFirstName && enrollmentData.intakeLastName) {
      // Only populate if fields are empty/undefined and we have Step2 data
      if (!enrollmentData.injuredPartyFirstName) {
        updateEnrollmentData('injuredPartyFirstName', enrollmentData.intakeFirstName)
      }
      if (!enrollmentData.injuredPartyLastName) {
        updateEnrollmentData('injuredPartyLastName', enrollmentData.intakeLastName)
      }
      setHasAutoPopulated(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount

  // Separate useEffect for other auto-population logic
  useEffect(() => {
    if (!enrollmentData.injuredPartyEmail && enrollmentData.intakeEmail) {
      updateEnrollmentData('injuredPartyEmail', enrollmentData.intakeEmail)
    }
    if (!enrollmentData.injuredPartyAddressLine1 && enrollmentData.intakeAddressLine1) {
      updateEnrollmentData('injuredPartyAddressLine1', enrollmentData.intakeAddressLine1)
    }
    if (!enrollmentData.injuredPartyAddressLine2 && enrollmentData.intakeAddressLine2) {
      updateEnrollmentData('injuredPartyAddressLine2', enrollmentData.intakeAddressLine2)
    }
    if (!enrollmentData.injuredPartyCity && enrollmentData.intakeCity) {
      updateEnrollmentData('injuredPartyCity', enrollmentData.intakeCity)
    }
    if (!enrollmentData.injuredPartyState && enrollmentData.intakeState) {
      updateEnrollmentData('injuredPartyState', enrollmentData.intakeState)
    }
    if (!enrollmentData.injuredPartyZipCode && enrollmentData.intakeZipCode) {
      updateEnrollmentData('injuredPartyZipCode', enrollmentData.intakeZipCode)
    }
    // Only auto-populate phoneNumber once on initial mount, not when user clears it
    if (!hasAutoPopulated && !enrollmentData.phoneNumber) {
      // Try intakePhoneNumber first (Gaming Addiction), then intakePhone (Roblox)
      if (enrollmentData.intakePhoneNumber) {
        updateEnrollmentData('phoneNumber', enrollmentData.intakePhoneNumber)
      } else if (enrollmentData.intakePhone) {
        updateEnrollmentData('phoneNumber', enrollmentData.intakePhone)
      }
    }
    // Auto-populate injured party DOB from Step2 birth info
    if (!enrollmentData.injuredPartyDOB && enrollmentData.birthMonth && enrollmentData.birthDay && enrollmentData.birthYear) {
      const month = enrollmentData.birthMonth.toString().padStart(2, '0')
      const day = enrollmentData.birthDay.toString().padStart(2, '0')
      const year = enrollmentData.birthYear
      const dob = `${year}-${month}-${day}`
      updateEnrollmentData('injuredPartyDOB', dob)
    }
    // Fallback: if there's an intakeDOB field, use that
    if (!enrollmentData.injuredPartyDOB && enrollmentData.intakeDOB) {
      updateEnrollmentData('injuredPartyDOB', enrollmentData.intakeDOB)
    }
    if (!enrollmentData.gender && enrollmentData.intakeGender) {
      updateEnrollmentData('gender', enrollmentData.intakeGender)
    }
    if (!enrollmentData.relationToVictim && enrollmentData.intakeRelationToVictim) {
      updateEnrollmentData('relationToVictim', enrollmentData.intakeRelationToVictim)
    }
    if (!enrollmentData.hasLegalAuthorization && enrollmentData.intakeHasLegalAuthorization !== undefined) {
      updateEnrollmentData('hasLegalAuthorization', enrollmentData.intakeHasLegalAuthorization)
    }
    
    // Auto-populate caller number type (default to Cell Phone)
    if (!enrollmentData.callerNumberType) {
      updateEnrollmentData('callerNumberType', 'Cell Phone')
    }
    
    // Auto-populate text messaging approval (default to Yes when Cell Phone is selected)
    if (enrollmentData.callerNumberType === 'Cell Phone' && (enrollmentData.approvesTextMessaging === undefined || enrollmentData.approvesTextMessaging === null)) {
      updateEnrollmentData('approvesTextMessaging', true)
    }

    // Auto-populate fields when "Myself" is selected in Step 2
    if (enrollmentData.relationToVictim?.toLowerCase() === 'myself') {
      // Auto-populate legal authorization as "Yes"
      if (enrollmentData.hasLegalAuthorization === undefined || enrollmentData.hasLegalAuthorization === null) {
        updateEnrollmentData('hasLegalAuthorization', true)
      }
      
      // Auto-populate claimant deceased as "No"
      if (enrollmentData.isClaimantDeceased === undefined || enrollmentData.isClaimantDeceased === null) {
        updateEnrollmentData('isClaimantDeceased', false)
      }
      
      // Auto-populate minor status as "No"
      if (enrollmentData.isMinor === undefined || enrollmentData.isMinor === null) {
        updateEnrollmentData('isMinor', false)
      }
    }
    
    // Auto-populate fields when "Loved one" is selected in Step 2
    if (enrollmentData.relationToVictim?.toLowerCase() === 'loved one') {
      // Auto-populate minor status as "Yes" for loved one cases
      if (enrollmentData.isMinor === undefined || enrollmentData.isMinor === null) {
        updateEnrollmentData('isMinor', true)
      }
    }
    if (!enrollmentData.isClaimantDeceased && enrollmentData.intakeIsClaimantDeceased !== undefined) {
      updateEnrollmentData('isClaimantDeceased', enrollmentData.intakeIsClaimantDeceased)
    }
    if (!enrollmentData.isMinor && enrollmentData.intakeIsMinor !== undefined) {
      updateEnrollmentData('isMinor', enrollmentData.intakeIsMinor)
    }
    if (!enrollmentData.hasAttorneyRepresentation && enrollmentData.intakeHasAttorneyRepresentation !== undefined) {
      updateEnrollmentData('hasAttorneyRepresentation', enrollmentData.intakeHasAttorneyRepresentation)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on specific fields that should trigger auto-population
    enrollmentData.intakeEmail,
    enrollmentData.intakeAddressLine1,
    enrollmentData.intakeCity,
    enrollmentData.intakeState,
    enrollmentData.intakeZipCode,
    enrollmentData.intakePhoneNumber,
    enrollmentData.intakePhone,
    enrollmentData.birthMonth,
    enrollmentData.birthDay,
    enrollmentData.birthYear,
    enrollmentData.intakeDOB,
    enrollmentData.intakeGender,
    enrollmentData.relationToVictim,
    enrollmentData.intakeHasLegalAuthorization,
    enrollmentData.callerNumberType
  ])

  // Auto-populate retainer agreement full name
  useEffect(() => {
    if (!enrollmentData.retainerAgreementFullName && enrollmentData.intakeFirstName && enrollmentData.intakeLastName) {
      updateEnrollmentData('retainerAgreementFullName', `${enrollmentData.intakeFirstName} ${enrollmentData.intakeLastName}`)
    }
  }, [enrollmentData.intakeFirstName, enrollmentData.intakeLastName, enrollmentData.retainerAgreementFullName, updateEnrollmentData])


  // Auto-populate agent name
  useEffect(() => {
    if (!enrollmentData.agentName) {
      updateEnrollmentData('agentName', 'Current Agent')
    }
  }, [enrollmentData.agentName, updateEnrollmentData])

  // Clear text messaging approval when caller number type changes to non-cell phone
  useEffect(() => {
    if (enrollmentData.callerNumberType !== 'Cell Phone' && enrollmentData.textMessagingApproval !== null) {
      updateEnrollmentData('textMessagingApproval', null)
    }
  }, [enrollmentData.callerNumberType, enrollmentData.textMessagingApproval, updateEnrollmentData])

  const handleContinue = async () => {
    const isValid = runValidation()
    if (!isValid) {
      return
    }

    try {
      if (handleNavigateWithCompletion) {
        await handleNavigateWithCompletion(computedNextStepIndex)
      } else {
        await handleNavigate(computedNextStepIndex)
      }
    } catch (error) {
      console.error('❌ Step3InjuredParty - Navigation failed:', error)
      alert('Failed to save your progress. Please try again.')
    }
  }

  const handleBack = () => {
    setErrors({})

    const targetIndex = computedPreviousStepIndex
    if (handleSimpleNavigate) {
      handleSimpleNavigate(targetIndex)
    } else {
      handleNavigate(targetIndex)
    }
  }

  const runValidation = () => {
    // Use campaign-specific validation
    const campaignName = enrollmentData.campaign?.name || ''
    const isPFASCampaign = campaignName.toLowerCase().includes('pfas') || 
                          campaignName.toLowerCase().includes('drinking water')
    
    const validationErrors = isPFASCampaign ? validatePFASStep(enrollmentData) : validateStep3(enrollmentData)
    console.log('Validation errors:', validationErrors)
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      return true
    }

    handleValidationErrors(validationErrors)
    return false
  }

  const handleStageNext = async () => runValidation()

  const handleStageBack = () => {
    setErrors({})
  }

  const formContent = (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <User className="w-6 h-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-accent-900">Injured Party Details</h3>
          <p className="text-sm text-accent-600">Collect essential information about the injured party.</p>
        </div>
      </div>

      <div className="space-y-6">
        <PersonalInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <ContactInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <IdentityInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <LegalInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />
      </div>
    </div>
  )

  if (shouldUseStageWrapper) {
    return (
      <StageWrapper
        currentStepIndex={computedCurrentStepIndex}
        totalSteps={computedTotalSteps}
        handleNavigate={handleStageNavigate}
        handleNavigateWithCompletion={handleNavigateWithCompletion}
        onNext={handleStageNext}
        onBack={handleStageBack}
        isNavigating={isNavigating}
        isCompleting={isCompleting}
      >
        {formContent}
      </StageWrapper>
    )
  }

  return (
    <div className="space-y-6">
      {formContent}

      <div className="flex justify-between">
        <button
          className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
          onClick={handleBack}
          disabled={isNavigating}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <button
          className="btn-primary flex items-center space-x-2 min-w-[120px] justify-center"
          onClick={handleContinue}
          disabled={isNavigating}
        >
          {isNavigating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}